import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, ShieldCheck, ArrowUpRight, BadgeCheck, Youtube } from "lucide-react";
import api from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";
import YouTubeEmbed from "@/components/YouTubeEmbed";

const DAIKIN_SHORTS = [
  "https://youtube.com/shorts/FWUZOg4VMkU",
  "https://youtube.com/shorts/IhLp6kMYJN0",
];

const DaikinProductCard = ({ p, testId }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="arel-card overflow-hidden rounded-2xl"
      data-testid={testId}
    >
      {/* Header with Daikin brand + product image */}
      <div className="relative flex aspect-square items-center justify-center overflow-hidden bg-gradient-to-br from-[#e6f6fe] to-white">
        <img
          src="/assets/logo/daikin-logo.png"
          alt="Daikin"
          className="absolute left-6 top-6 z-10 h-6 w-auto opacity-90"
        />
        <img
          src={p.image_url}
          alt={p.name}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.src = "/assets/logo/daikin-logo.png";
            e.currentTarget.className = "h-full w-auto max-w-[70%] object-contain";
          }}
        />
        <div className="absolute right-6 top-6 z-10 rounded-full bg-[#0097e0] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
          Daikin
        </div>
      </div>

      <div className="p-6 md:p-7">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#0097e0]">
          Model · {p.model}
        </div>
        <div className="mt-2 font-display text-xl font-bold leading-tight text-ink md:text-2xl">
          {p.name}
        </div>

        {/* Chips: capacity, rating */}
        <div className="mt-4 flex flex-wrap gap-2">
          {p.specifications?.Capacity && (
            <span className="rounded-full border border-line-strong px-3 py-1 text-xs font-semibold text-ink">
              {p.specifications.Capacity}
            </span>
          )}
          {p.specifications?.["Energy Rating"] && (
            <span className="rounded-full border border-line-strong bg-[#fff9e6] px-3 py-1 text-xs font-semibold text-[#a97600]">
              {p.specifications["Energy Rating"]}
            </span>
          )}
          {p.specifications?.Type && (
            <span className="rounded-full border border-line-strong px-3 py-1 text-xs font-semibold text-ink">
              {p.specifications.Type}
            </span>
          )}
        </div>

        {/* Highlights (short bullets) */}
        <ul className="mt-6 space-y-2">
          {(p.highlights && p.highlights.length > 0
            ? p.highlights
            : p.features.slice(0, 4)).map((h, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm text-ink-2"
            >
              <BadgeCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#0097e0]" />
              {h}
            </li>
          ))}
        </ul>

        {/* Warranty */}
        {p.warranty && (
          <div className="mt-5 flex items-center gap-2 rounded-lg border border-[#0097e0]/30 bg-[#e6f6fe] p-3 text-xs">
            <ShieldCheck className="h-4 w-4 flex-shrink-0 text-[#0097e0]" />
            <span className="text-ink-2">{p.warranty}</span>
          </div>
        )}

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((e) => !e)}
          data-testid={`daikin-toggle-${p.model}`}
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0097e0] hover:text-[#0079b3]"
        >
          {expanded ? (
            <>Hide full specs <ChevronUp className="h-4 w-4" /></>
          ) : (
            <>View full specs <ChevronDown className="h-4 w-4" /></>
          )}
        </button>

        {expanded && (
          <div
            className="mt-4 space-y-4"
            data-testid={`daikin-fullspecs-${p.model}`}
          >
            {/* Description */}
            <p className="text-sm leading-relaxed text-muted-warm">
              {p.description}
            </p>

            {/* All features */}
            {p.features && p.features.length > 0 && (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-warm">
                  All features
                </div>
                <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {p.features.map((f, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-ink-2"
                    >
                      <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-[#0097e0]" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Spec table */}
            {p.specifications &&
              Object.keys(p.specifications).length > 0 && (
                <div className="rounded-lg border border-line bg-[#f7fbfe] p-4">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-muted-warm">
                    Technical Specifications
                  </div>
                  <dl className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {Object.entries(p.specifications).map(([k, v]) => (
                      <div
                        key={k}
                        className="flex justify-between gap-3 border-b border-line pb-1.5"
                      >
                        <dt className="text-xs text-muted-warm">{k}</dt>
                        <dd className="text-xs font-semibold text-ink text-right">
                          {v}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
          </div>
        )}

        <Link
          to={`/products/${p.id}`}
          data-testid={`daikin-inquire-link-${p.model}`}
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#dd0447]"
        >
          Inquire about this model <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

const DaikinPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api
      .get("/products?brand=Daikin")
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="daikin-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-line bg-gradient-to-br from-[#e6f6fe] via-[#f7fbfe] to-white">
        <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 rounded-full bg-[#0097e0]/20 blur-[120px]" />
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-24 lg:px-10">
          <img
            src="/assets/logo/daikin-logo.png"
            alt="Daikin"
            className="mb-6 h-14 w-auto md:h-16"
          />
          <div className="text-xs font-bold uppercase tracking-[0.28em] text-[#0097e0]">
            Official Importer · Nepal
          </div>
          <h1 className="mt-4 font-display max-w-4xl text-4xl font-black leading-[0.98] tracking-tight text-ink md:text-6xl lg:text-7xl">
            AREL Nepal is the official importer of{" "}
            <span className="text-[#0097e0]">Daikin Air Conditioners</span> in
            Nepal.
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink-2 md:text-lg">
            Daikin is the world&apos;s leading air-conditioning brand, trusted
            for its patented Swing Compressor, energy efficiency and
            all-weather performance. Through AREL Nepal, Nepali homes and
            businesses now get access to authentic Daikin split ACs — with
            local warranty, service and dealer support.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#daikin-lineup"
              data-testid="daikin-see-lineup-btn"
              className="rounded-full bg-[#0097e0] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0079b3]"
            >
              See the 2026 Line-up
            </a>
            <Link
              to="/contact"
              data-testid="daikin-inquire-btn"
              className="arel-outline-btn rounded-full px-6 py-3 text-sm font-semibold"
            >
              Enquire Now
            </Link>
          </div>
        </div>
      </section>

      {/* Products grid */}
      <section
        id="daikin-lineup"
        className="mx-auto max-w-7xl px-6 py-20 lg:px-10"
      >
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.28em] text-[#0097e0]">
              2026 Line-up
            </div>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-ink md:text-4xl">
              Daikin Split ACs available in Nepal
            </h2>
          </div>
          <div className="text-sm text-muted-warm">
            {products.length} model{products.length !== 1 ? "s" : ""}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-warm">Loading Daikin line-up…</div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {products.map((p) => (
              <DaikinProductCard
                key={p.id}
                p={p}
                testId={`daikin-product-${p.model}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Daikin YouTube Shorts */}
      <section className="border-t border-line bg-[#f7fbfe] py-20" data-testid="daikin-shorts-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.28em] text-[#0097e0]">
                <Youtube className="h-4 w-4" /> Daikin in Action
              </div>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-ink md:text-4xl">
                Watch Daikin Shorts
              </h2>
              <p className="mt-3 max-w-xl text-sm text-muted-warm">
                Quick, useful clips about Daikin technology — right on the page,
                no need to leave the site.
              </p>
            </div>
          </div>
          <div className="mx-auto grid max-w-2xl gap-6 sm:grid-cols-2">
            {DAIKIN_SHORTS.map((url, i) => (
              <div
                key={url}
                data-testid={`daikin-short-${i}`}
                className="mx-auto w-full max-w-[280px]"
              >
                <YouTubeEmbed
                  url={url}
                  aspectClass="aspect-[9/16]"
                  title={`Daikin YouTube Short ${i + 1}`}
                  testId={`daikin-short-embed-${i}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dealer / Enquire CTA */}
      <section className="border-t border-line bg-[#f4f1ec] py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div className="arel-card rounded-2xl p-8 md:p-10">
            <div className="text-xs font-bold uppercase tracking-[0.28em] text-[#0097e0]">
              Enquire about Daikin
            </div>
            <div className="mt-3 font-display text-3xl font-bold text-ink">
              Get pricing, availability & installation.
            </div>
            <p className="mt-3 text-sm text-muted-warm">
              Send us your details and our Daikin specialists will contact
              you within 24 hours.
            </p>
            <div className="mt-8">
              <InquiryForm
                inquiryType="daikin"
                subject="Daikin AC Enquiry"
                testIdPrefix="daikin-page"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DaikinPage;
